// UC20 — the AMDM nomenclator record behind one medicine code (spec 061,
// docs/shape-uc-20-single-source.md).
//
// The sync keeps the row exactly as AMDM served it next to the columns it maps. This
// section lists that row key by key, under AMDM's own field names, so the System
// Administrator can see what AMDM said without a second tool. Nothing here is
// interpreted: a string is shown verbatim, a number or boolean as its text, an empty
// value as the register's empty cell.
import { useTranslate } from '@tolgee/react';
import { EMPTY_CELL, formatDateTime } from '@simsm/ui-shared';
import { FormRow, FormSection, FormValue } from '../../components/FormPage/FormPage';

/** One served value as the text the page shows. */
export function formatSourceValue(value: unknown): string {
  if (value === null || value === undefined) return EMPTY_CELL;
  if (typeof value === 'string') return value === '' ? EMPTY_CELL : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  // The feed serves scalars; a nested value would be a feed change, shown whole and not hidden.
  return JSON.stringify(value);
}

export function MedicineSourceRecord({
  record,
  receivedAtIso,
}: {
  /** The row as AMDM served it; null when no sync wrote this code. */
  record: Record<string, unknown> | null;
  receivedAtIso: string | null;
}): JSX.Element {
  const { t } = useTranslate();
  const entries = record ? Object.entries(record) : [];
  const rows: [string, unknown][][] = [];
  for (let i = 0; i < entries.length; i += 2) rows.push(entries.slice(i, i + 2));

  return (
    <FormSection
      title={t('medicines.section.source_record')}
      lede={t('medicines.section.source_record_lede')}
    >
      {record === null ? (
        <p className="simsm-form-page__field-value" data-testid="source-record-none">
          {t('medicines.source_record.none')}
        </p>
      ) : (
        <>
          <FormRow as="dl">
            <FormValue label={t('medicines.source_record.received')}>
              {receivedAtIso ? formatDateTime(receivedAtIso) : EMPTY_CELL}
            </FormValue>
          </FormRow>
          {rows.map((pair) => (
            <FormRow key={pair.map(([name]) => name).join('|')} columns={2} as="dl">
              {pair.map(([name, value]) => (
                <FormValue key={name} label={name}>
                  {formatSourceValue(value)}
                </FormValue>
              ))}
            </FormRow>
          ))}
        </>
      )}
    </FormSection>
  );
}
